import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { Box, Button, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { paths } from '@common/types';
import { useGeneral } from './hooks';
import { ImageUploader } from 'components/ImageUploader';
import { USStateSelect } from 'components/USStateSelect';
import { FormGooglePlacesAutocomplete, SaveChangesModal } from '@components';
import { Select } from 'ui';

interface GeneralProps {
  operator: paths[`/operator/{record_id}`]['get']['responses']['200']['content']['application/json'];
}

export const General = ({ operator }: GeneralProps) => {
  const {
    formMethods,
    onSubmit,
    isSubmitEnabled,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    isSubmitting,
    organizationOptions,
    partnerOptions,
    setValue,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useGeneral(operator);

  const { control } = formMethods;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
            gap: '1rem',
          }}
        >
          <ImageUploader
            onDelete={handleDeleteLogo}
            croppedImage={logo}
            setCroppedImage={handleUploadLogo}
          />

          <Controller
            name='name'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.operators.addModal.name')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />

          <Controller
            name='organization_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                label={t('adminPanel.operators.addModal.organization')}
                error={!isNil(error)}
                required
                disabled
                onChange={event => {
                  field.onChange(event);
                  setValue('partner_id', '');
                }}
                options={organizationOptions}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='partner_id'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                label={t('adminPanel.operators.addModal.partner')}
                variant='outlined'
                error={!isNil(error)}
                required
                disabled
                options={partnerOptions}
                helperText={error?.message}
              />
            )}
          />

          <Typography
            variant='subtitle2'
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            {t('adminPanel.operators.addModal.contactInformation')}
          </Typography>

          <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='street'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.addressLine1')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='suite'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.addressLine2')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: '0.5rem',
            }}
          >
            <Controller
              name='city'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.operators.addModal.city')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name='state'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <USStateSelect
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ''}
                  required
                />
              )}
            />
          </Box>

          <Controller
            name='zip_code'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.operators.addModal.zip')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                required
                fullWidth
              />
            )}
          />

          <Controller
            name='county'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('adminPanel.operators.addModal.county')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              pt: '0.5rem',
              position: 'sticky',
              bottom: '0',
              left: 0,
              bgcolor: theme => theme.palette.common.white,
              zIndex: 10,
            }}
          >
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={!isSubmitEnabled}
              loading={isSubmitting}
              loadingPosition='center'
            >
              {t('general.saveChanges')}
            </Button>
          </Box>
        </Form>
      </FormProvider>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </Box>
  );
};
